import { createFileRoute, notFound } from '@tanstack/react-router';
import { motion } from 'motion/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ClaimPlayerDialog } from '@/components/features/players/ClaimPlayerDialog';
import { PlayerFormDialog } from '@/components/features/players/PlayerFormDialog';
import { PlayerMergeHistorySheet } from '@/components/features/players/PlayerMergeHistorySheet';
import { PlayerStatsDashboard } from '@/components/features/players/PlayerStatsDashboard';
import { Button } from '@/components/ui/button';
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
} from '@/components/ui/responsive-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { claimPlayer, mergePlayers, unclaimPlayer, unmergePlayers } from '@/db/players';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { usePlayerMergeTargets } from '@/hooks/usePlayerMergeHistory';
import { usePlayers } from '@/hooks/usePlayers';
import { usePlayerStats } from '@/hooks/usePlayerStats';
import { useRouteAnimation } from '@/hooks/useRouteAnimation';
import { handleUserFacingError } from '@/lib/error-handler';
import { fadeIn, transitions } from '@/lib/motion-config';
import { getErrorMessage } from '@/lib/sync/error-utils';
import { toastError, toastSuccess, toastSuccessWithUndo } from '@/lib/toast-helpers';
import type { EventId, Player, PlayerId, UserId } from '@/schemas';
import { parsePlayerId, safeParsePlayerId } from '@/schemas';
import { useAuthStore } from '@/stores';

export const Route = createFileRoute('/players/$playerId')({
  loader: ({ params }) => {
    const parseResult = safeParsePlayerId(params.playerId);
    if (!parseResult.success) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error -- notFound() is TanStack Router's official API for 404 handling, not a standard Error
      throw notFound();
    }
  },
  component: PlayerDashboardRoute,
});

interface MergeDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly targetPlayer: Player | undefined;
  readonly availableCombinePlayers: Player[];
  readonly sourcePlayerId: PlayerId | undefined;
  readonly selectedCombinePlayer: Player | undefined;
  readonly isMerging: boolean;
  readonly mergeError: string | undefined;
  readonly onSourcePlayerChange: (playerId: PlayerId | undefined) => void;
  readonly onConfirm: () => Promise<void>;
  readonly onCancel: () => void;
  readonly t: ReturnType<typeof useTranslation>['t'];
}

function MergeDialog({
  open,
  onOpenChange,
  targetPlayer,
  availableCombinePlayers,
  sourcePlayerId,
  selectedCombinePlayer,
  isMerging,
  mergeError,
  onSourcePlayerChange,
  onConfirm,
  onCancel,
  t,
}: MergeDialogProps) {
  return (
    <ResponsiveDialog open={open} onOpenChange={onOpenChange}>
      <ResponsiveDialogContent className="sm:max-w-lg">
        <div className="space-y-6 px-4 sm:px-0">
          <ResponsiveDialogHeader className="space-y-2 text-left">
            <ResponsiveDialogTitle>{t('players.mergePlayers')}</ResponsiveDialogTitle>
            <ResponsiveDialogDescription>
              {targetPlayer
                ? t('players.mergeDescriptionTarget', {
                    playerName: targetPlayer.name,
                  } as { playerName: string })
                : t('players.mergeDescription')}
            </ResponsiveDialogDescription>
          </ResponsiveDialogHeader>
          <div className="space-y-4">
            <div className="rounded-lg border border-border bg-muted/20 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t('players.mergeKeepLabel')}
              </p>
              <p className="mt-2 text-lg font-semibold leading-6">
                {targetPlayer?.name ?? t('players.mergeKeepFallback')}
              </p>
              <p className="mt-2 text-sm text-muted-foreground">{t('players.mergeKeepHelper')}</p>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium" htmlFor="merge-player-combine">
                {t('players.mergeCombineLabel')}
              </label>
              {availableCombinePlayers.length === 0 ? (
                <p className="text-sm italic text-muted-foreground">
                  {t('players.noTargetPlayers')}
                </p>
              ) : (
                <>
                  <Select
                    value={sourcePlayerId ? String(sourcePlayerId) : ''}
                    onValueChange={(value) => {
                      if (value) {
                        onSourcePlayerChange(parsePlayerId(value));
                      } else {
                        onSourcePlayerChange(undefined);
                      }
                    }}
                    disabled={isMerging}
                  >
                    <SelectTrigger
                      id="merge-player-combine"
                      aria-label={t('players.mergeCombineLabel')}
                    >
                      <SelectValue placeholder={t('players.mergeSelectCombine')} />
                    </SelectTrigger>
                    <SelectContent>
                      {availableCombinePlayers.map((player) => (
                        <SelectItem key={player.id} value={String(player.id)}>
                          {player.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {selectedCombinePlayer
                      ? t('players.mergeCombineSelected', {
                          sourceName: selectedCombinePlayer.name,
                          targetName: targetPlayer?.name ?? t('players.mergeKeepFallback'),
                        } as { sourceName: string; targetName: string })
                      : t('players.mergeCombineHelper')}
                  </p>
                </>
              )}
            </div>
            {mergeError && <p className="text-sm text-destructive">{mergeError}</p>}
          </div>
        </div>
        <ResponsiveDialogFooter className="px-4 sm:px-0">
          <Button variant="outline" onClick={onCancel} disabled={isMerging}>
            {t('cancel')}
          </Button>
          <Button
            onClick={() => {
              onConfirm().catch((error: unknown) => {
                console.error('Merge confirmation error:', error);
              });
            }}
            disabled={isMerging || !sourcePlayerId || availableCombinePlayers.length === 0}
          >
            {isMerging ? t('players.merging') : t('players.merge')}
          </Button>
        </ResponsiveDialogFooter>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
}

function usePlayerActionHandlers(
  userId: string | undefined,
  t: ReturnType<typeof useTranslation<'common'>>['t'],
  refreshPlayers: () => Promise<void>,
) {
  const [showPlayerFormDialog, setShowPlayerFormDialog] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState<Player | undefined>();
  const [claimPlayerDialogOpen, setClaimPlayerDialogOpen] = useState(false);
  const [claimPlayerDialogPlayer, setClaimPlayerDialogPlayer] = useState<Player | undefined>();
  const [showMergeDialog, setShowMergeDialog] = useState(false);
  const [sourcePlayerId, setSourcePlayerId] = useState<PlayerId | undefined>();
  const [targetPlayerId, setTargetPlayerId] = useState<PlayerId | undefined>();
  const [isMerging, setIsMerging] = useState(false);
  const [mergeError, setMergeError] = useState<string | undefined>();
  const [mergeHistoryPlayer, setMergeHistoryPlayer] = useState<Player | undefined>();
  const [showMergeHistorySheet, setShowMergeHistorySheet] = useState(false);

  const handleClaimWithHandle = useCallback(
    async (handle: string, playerId: PlayerId, _userId: string): Promise<void> => {
      const { reserveHandle } = await import('@/lib/supabase/handles');
      const { clearHandleRelease } = await import('@/lib/supabase/handle-releases');
      await reserveHandle(handle, playerId);
      clearHandleRelease(handle, playerId);
    },
    [],
  );

  const handleClaimWithHandleFallback = useCallback(
    async (playerId: PlayerId, _userId: string): Promise<void> => {
      await claimPlayer(playerId, _userId as UserId);
      await refreshPlayers();
      toastSuccess(t('players.claimSuccess', 'Player claimed'));
    },
    [t, refreshPlayers],
  );

  const handleClaimWithHandleError = useCallback(
    async (error: unknown, playerId: PlayerId, userId: string): Promise<void> => {
      console.warn('Failed to reserve handle during claim:', error);
      const errorMessage = handleUserFacingError(
        error,
        t('players.claimHandleError', 'Failed to reserve handle'),
      );
      toastError(errorMessage);
      await handleClaimWithHandleFallback(playerId, userId);
    },
    [t, handleClaimWithHandleFallback],
  );

  const handleClaimWithoutHandle = useCallback(
    async (playerId: PlayerId, userId: string): Promise<void> => {
      await claimPlayer(playerId, userId as UserId);
      await refreshPlayers();
      toastSuccess(t('players.claimSuccess', 'Player claimed'));
    },
    [t, refreshPlayers],
  );

  const handleEditClick = useCallback((player: Player) => {
    setEditingPlayer(player);
    setShowPlayerFormDialog(true);
  }, []);

  const handleClaimClick = useCallback(
    (player: Player): Promise<void> => {
      if (!userId) {
        toastError(t('players.claimRequiresAuth', 'You must be signed in to claim a player'));
        return Promise.resolve();
      }

      setClaimPlayerDialogPlayer(player);
      setClaimPlayerDialogOpen(true);
      return Promise.resolve();
    },
    [userId, t],
  );

  const handleClaimWithHandleReservation = useCallback(
    async (handle: string, playerId: PlayerId, userId: string): Promise<boolean> => {
      try {
        await handleClaimWithHandle(handle, playerId, userId);
        return true;
      } catch (error) {
        await handleClaimWithHandleError(error, playerId, userId);
        return false;
      }
    },
    [handleClaimWithHandle, handleClaimWithHandleError],
  );

  const handleClaimConfirm = useCallback(
    async (handle?: string) => {
      if (!claimPlayerDialogPlayer || !userId) {
        return;
      }

      try {
        if (handle) {
          const success = await handleClaimWithHandleReservation(
            handle,
            claimPlayerDialogPlayer.id,
            userId,
          );
          if (!success) {
            return;
          }
        }

        await handleClaimWithoutHandle(claimPlayerDialogPlayer.id, userId);
      } catch (error) {
        const errorMessage = handleUserFacingError(
          error,
          t('players.claimError', 'Failed to claim player'),
        );
        toastError(errorMessage);
        throw error;
      }
    },
    [
      claimPlayerDialogPlayer,
      userId,
      handleClaimWithHandleReservation,
      handleClaimWithoutHandle,
      t,
    ],
  );

  const handleUnclaimClick = useCallback(
    async (player: Player) => {
      try {
        await unclaimPlayer(player.id);
        await refreshPlayers();
        toastSuccess(t('players.unclaimSuccess', 'Player unclaimed'));
      } catch (error) {
        const errorMessage = handleUserFacingError(
          error,
          t('players.unclaimError', 'Failed to unclaim player'),
          { operation: 'unclaimPlayer', playerId: player.id },
        );
        toastError(errorMessage);
        throw error;
      }
    },
    [t, refreshPlayers],
  );

  const handleMergeClick = useCallback((player: Player) => {
    setTargetPlayerId(player.id);
    setSourcePlayerId(undefined);
    setMergeError(undefined);
    setShowMergeDialog(true);
  }, []);

  const handleViewMergeHistory = useCallback((playerToInspect: Player) => {
    setMergeHistoryPlayer(playerToInspect);
    setShowMergeHistorySheet(true);
  }, []);

  return {
    showPlayerFormDialog,
    setShowPlayerFormDialog,
    editingPlayer,
    setEditingPlayer,
    claimPlayerDialogOpen,
    setClaimPlayerDialogOpen,
    claimPlayerDialogPlayer,
    setClaimPlayerDialogPlayer,
    showMergeDialog,
    setShowMergeDialog,
    sourcePlayerId,
    setSourcePlayerId,
    targetPlayerId,
    setTargetPlayerId,
    isMerging,
    setIsMerging,
    mergeError,
    setMergeError,
    mergeHistoryPlayer,
    setMergeHistoryPlayer,
    showMergeHistorySheet,
    setShowMergeHistorySheet,
    handleEditClick,
    handleClaimClick,
    handleClaimConfirm,
    handleUnclaimClick,
    handleMergeClick,
    handleViewMergeHistory,
  };
}

function usePlayerActionState(
  resolvedPlayer: Player | undefined,
  players: Player[],
  mergeTargetIds: Set<PlayerId>,
  userId: string | undefined,
) {
  const hasMergeHistory = useMemo(
    () => resolvedPlayer && mergeTargetIds.has(resolvedPlayer.id),
    [resolvedPlayer, mergeTargetIds],
  );

  const showClaimOption = useMemo(
    () => resolvedPlayer && !resolvedPlayer.userId && Boolean(userId),
    [resolvedPlayer, userId],
  );

  const showUnclaimOption = useMemo(
    () =>
      resolvedPlayer &&
      Boolean(userId) &&
      Boolean(resolvedPlayer.userId) &&
      resolvedPlayer.userId === userId,
    [resolvedPlayer, userId],
  );

  const hasAvailableMergeTargets = useMemo(
    () =>
      resolvedPlayer &&
      players.some((otherPlayer) => otherPlayer.id !== resolvedPlayer.id && !otherPlayer.deletedAt),
    [resolvedPlayer, players],
  );

  return {
    hasMergeHistory: hasMergeHistory ?? false,
    showClaimOption: showClaimOption ?? false,
    showUnclaimOption: showUnclaimOption ?? false,
    hasAvailableMergeTargets: hasAvailableMergeTargets ?? false,
  };
}

// eslint-disable-next-line sonarjs/cognitive-complexity -- PlayerDashboardRoute handles multiple complex flows (merge, claim, stats display) which increases complexity. Further reduction would require significant refactoring into smaller components.
function PlayerDashboardRoute() {
  const { playerId: playerIdParam } = Route.useParams();
  const { t } = useTranslation('common');
  const userId = useAuthStore((state) => state.userId);

  const playerId = parsePlayerId(playerIdParam);

  const stats = usePlayerStats(playerId, t);
  const { players, refresh: refreshPlayers } = usePlayers({ includeDeleted: false });
  const { targetIds: mergeTargetIds } = usePlayerMergeTargets();

  const actionHandlers = usePlayerActionHandlers(userId, t, refreshPlayers);

  const documentTitle = useMemo(() => {
    const playerName = stats.player?.name ?? stats.fallbackPlayer?.name;
    if (playerName) {
      return `${playerName} - ${t('players.stats.title', 'Player stats')} · ${t(
        'app.name',
        'One Score',
      )}`;
    }

    return `${t('players.stats.title', 'Player stats')} · ${t('app.name', 'One Score')}`;
  }, [stats.player?.name, stats.fallbackPlayer?.name, t]);

  useDocumentTitle(documentTitle);

  const resolvedPlayer = stats.player ?? stats.fallbackPlayer;

  const actionState = usePlayerActionState(resolvedPlayer, players, mergeTargetIds, userId);

  const handleMergeUndo = useCallback(
    async (mergeEventId: string) => {
      try {
        await unmergePlayers(mergeEventId as EventId);
        await refreshPlayers();
        toastSuccess(t('players.undoMergeSuccess'));
      } catch (error: unknown) {
        console.error('Failed to undo merge', error);
        toastError(t('players.undoMergeError'));
      }
    },
    [t, refreshPlayers],
  );

  const handleMergeSuccess = useCallback(
    (mergeEventId: string, keptPlayerName: string, combinedPlayerName: string | undefined) => {
      actionHandlers.setShowMergeDialog(false);
      actionHandlers.setSourcePlayerId(undefined);
      actionHandlers.setTargetPlayerId(undefined);
      void refreshPlayers();
      toastSuccessWithUndo(
        t('players.mergeSuccess', {
          targetName: keptPlayerName,
          sourceName: combinedPlayerName ?? t('players.mergeSelectFallback'),
        }),
        () => handleMergeUndo(mergeEventId),
        {
          undoLabel: t('players.undoMergeAction'),
        },
      );
    },
    [actionHandlers, t, refreshPlayers, handleMergeUndo],
  );

  const handleMergeConfirm = useCallback(async () => {
    if (!actionHandlers.sourcePlayerId || !actionHandlers.targetPlayerId) {
      actionHandlers.setMergeError(t('players.mergeMissingSelection'));
      return;
    }

    actionHandlers.setIsMerging(true);
    actionHandlers.setMergeError(undefined);

    const targetPlayer = players.find((p) => p.id === actionHandlers.targetPlayerId);
    const sourcePlayer = players.find((p) => p.id === actionHandlers.sourcePlayerId);
    const keptPlayerName = targetPlayer?.name ?? t('players.mergeKeepFallback');
    const combinedPlayerName = sourcePlayer?.name;

    try {
      const mergeEventId = await mergePlayers(
        actionHandlers.sourcePlayerId,
        actionHandlers.targetPlayerId,
      );
      handleMergeSuccess(mergeEventId, keptPlayerName, combinedPlayerName);
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error, t('players.mergeGenericError'));
      actionHandlers.setMergeError(errorMessage);
    } finally {
      actionHandlers.setIsMerging(false);
    }
  }, [actionHandlers, players, t, handleMergeSuccess]);

  const handleMergeCancel = useCallback(() => {
    actionHandlers.setShowMergeDialog(false);
    actionHandlers.setSourcePlayerId(undefined);
    actionHandlers.setTargetPlayerId(undefined);
    actionHandlers.setMergeError(undefined);
  }, [actionHandlers]);

  // Computed values for merge dialog
  const targetPlayer = useMemo(
    () =>
      actionHandlers.targetPlayerId
        ? players.find((player) => player.id === actionHandlers.targetPlayerId)
        : undefined,
    [actionHandlers.targetPlayerId, players],
  );

  const availableCombinePlayers = useMemo(
    () =>
      players.filter((player) => player.id !== actionHandlers.targetPlayerId && !player.deletedAt),
    [players, actionHandlers.targetPlayerId],
  );

  const selectedCombinePlayer = useMemo(
    () =>
      actionHandlers.sourcePlayerId
        ? players.find((player) => player.id === actionHandlers.sourcePlayerId)
        : undefined,
    [actionHandlers.sourcePlayerId, players],
  );

  // Close merge history sheet if player no longer has merge history
  useEffect(() => {
    if (!actionHandlers.mergeHistoryPlayer) {
      return;
    }
    if (!actionHandlers.showMergeHistorySheet) {
      return;
    }

    if (!mergeTargetIds.has(actionHandlers.mergeHistoryPlayer.id)) {
      actionHandlers.setShowMergeHistorySheet(false);
      actionHandlers.setMergeHistoryPlayer(undefined);
    }
  }, [actionHandlers, mergeTargetIds]);

  const { shouldAnimate, routeKey } = useRouteAnimation();

  return (
    <motion.div
      key={routeKey}
      variants={fadeIn}
      initial={shouldAnimate ? 'initial' : false}
      animate="animate"
      transition={transitions.springGentle}
      className="mx-auto w-full max-w-5xl space-y-8 px-4 py-6"
    >
      <PlayerStatsDashboard
        player={stats.player}
        fallbackPlayer={stats.fallbackPlayer}
        overview={stats.overview}
        scoreTrend={stats.scoreTrend}
        favoriteGames={stats.favoriteGames}
        favoritePartners={stats.favoritePartners}
        recentSessions={stats.recentSessions}
        isLoading={stats.isLoading}
        error={stats.error}
        onEdit={resolvedPlayer ? () => actionHandlers.handleEditClick(resolvedPlayer) : undefined}
        onClaim={resolvedPlayer ? () => actionHandlers.handleClaimClick(resolvedPlayer) : undefined}
        onUnclaim={
          resolvedPlayer ? () => actionHandlers.handleUnclaimClick(resolvedPlayer) : undefined
        }
        onMerge={resolvedPlayer ? () => actionHandlers.handleMergeClick(resolvedPlayer) : undefined}
        onViewMergeHistory={
          resolvedPlayer ? () => actionHandlers.handleViewMergeHistory(resolvedPlayer) : undefined
        }
        hasMergeHistory={actionState.hasMergeHistory}
        showClaimOption={actionState.showClaimOption}
        showUnclaimOption={actionState.showUnclaimOption}
        hasAvailableMergeTargets={actionState.hasAvailableMergeTargets}
      />

      {/* Player Form Dialog */}
      <PlayerFormDialog
        open={actionHandlers.showPlayerFormDialog}
        onOpenChange={actionHandlers.setShowPlayerFormDialog}
        player={actionHandlers.editingPlayer}
        onSuccess={() => {
          void refreshPlayers();
        }}
      />

      {/* Claim Player Dialog */}
      {actionHandlers.claimPlayerDialogPlayer && userId && (
        <ClaimPlayerDialog
          open={actionHandlers.claimPlayerDialogOpen}
          onOpenChange={(open) => {
            actionHandlers.setClaimPlayerDialogOpen(open);
            if (!open) {
              actionHandlers.setClaimPlayerDialogPlayer(undefined);
            }
          }}
          player={actionHandlers.claimPlayerDialogPlayer}
          userId={userId}
          onConfirm={actionHandlers.handleClaimConfirm}
        />
      )}

      {/* Merge Dialog */}
      <MergeDialog
        open={actionHandlers.showMergeDialog}
        onOpenChange={actionHandlers.setShowMergeDialog}
        targetPlayer={targetPlayer}
        availableCombinePlayers={availableCombinePlayers}
        sourcePlayerId={actionHandlers.sourcePlayerId}
        selectedCombinePlayer={selectedCombinePlayer}
        isMerging={actionHandlers.isMerging}
        mergeError={actionHandlers.mergeError}
        onSourcePlayerChange={actionHandlers.setSourcePlayerId}
        onConfirm={handleMergeConfirm}
        onCancel={handleMergeCancel}
        t={t}
      />

      {/* Merge History Sheet */}
      <PlayerMergeHistorySheet
        open={actionHandlers.showMergeHistorySheet && Boolean(actionHandlers.mergeHistoryPlayer)}
        onOpenChange={(open) => {
          actionHandlers.setShowMergeHistorySheet(open);
          if (!open) {
            actionHandlers.setMergeHistoryPlayer(undefined);
          }
        }}
        player={actionHandlers.mergeHistoryPlayer}
      />
    </motion.div>
  );
}
